import React from "react"
import PropTypes from "prop-types"
import { useTranslation } from "i18n"

// Material UI
import { makeStyles } from "@material-ui/core/styles"
import {
  Box,
  Grid,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Slider,
  Checkbox,
  FormControlLabel,
  Tooltip
} from "@material-ui/core"

// Components
import Select from "components/shared/Select"

// Helpers
import { getPluralOrSingular } from "shared/helpers"

const useStyles = makeStyles(theme => ({
  selectRoot: {
    backgroundColor: "white",
    width: "100%",
    height: "0px"
  },
  formControl: {
    maxWidth: 260
  },
  capitalize: {
    textTransform: "capitalize"
  },
  fontWeightBolder: {
    fontWeight: theme.typography.fontWeightBolder
  }
}))

export default function Filters({
  loading,
  filters,
  category,
  handleCategoryFilter,
  handleFilters
}) {
  const { t } = useTranslation()

  const classes = useStyles()

  return (
    <Grid container direction="column" spacing={2}>
      {filters.category && filters.category.length > 1 && (
        <Grid item>
          <FormControl
            variant="outlined"
            className={classes.formControl}
            fullWidth
          >
            <InputLabel>Categories</InputLabel>
            <Select
              disabled={loading}
              value={category}
              onChange={handleCategoryFilter}
              label="Categories"
              classes={{
                root: classes.selectRoot
              }}
            >
              {filters.category.map((value, index) => (
                <MenuItem key={index} value={value.uuid}>
                  {value.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}

      {filters.language !== undefined && filters.language.length > 1 && (
        <Grid item container direction="column">
          <Grid item>
            <Typography className={classes.fontWeightBolder} variant="h6">
              {t("Language")}
            </Typography>
          </Grid>
          <Grid item container direction="column">
            {filters.language.map(value => (
              <Grid item key={value.uuid}>
                <FormControlLabel
                  className={classes.capitalize}
                  control={
                    <Checkbox
                      disabled={loading}
                      onChange={handleFilters}
                      name="language"
                      color="primary"
                      id={value.uuid}
                    />
                  }
                  label={value.name}
                />
              </Grid>
            ))}
          </Grid>
          <Grid item>
            <Divider />
          </Grid>
        </Grid>
      )}

      {filters.level !== undefined && filters.level.length > 0 && (
        <Grid item container direction="column">
          <Grid item>
            <Typography className={classes.fontWeightBolder} variant="h6">
              {t("Level")}
            </Typography>
          </Grid>
          <Grid item container direction="column">
            {filters.level.map((level, index) => (
              <Grid item key={index}>
                <FormControlLabel
                  className={classes.capitalize}
                  control={
                    <Checkbox
                      disabled={loading}
                      onChange={handleFilters}
                      name="level"
                      color="primary"
                      id={level.uuid ? level.uuid : level}
                    />
                  }
                  label={level.level ? level.level : level}
                />
              </Grid>
            ))}
          </Grid>
          <Grid item>
            <Divider />
          </Grid>
        </Grid>
      )}

      {filters.learning_mode !== undefined && filters.learning_mode.length > 0 && (
        <Grid item container direction="column">
          <Grid item>
            <Typography className={classes.fontWeightBolder} variant="h6">
              {t("Mode of studying")}
            </Typography>
          </Grid>
          <Grid item container direction="column">
            {filters.learning_mode.map(value => (
              <Grid item key={value.uuid}>
                <FormControlLabel
                  className={classes.capitalize}
                  control={
                    <Checkbox
                      disabled={loading}
                      onChange={handleFilters}
                      name="learning_mode"
                      color="primary"
                      id={value.uuid}
                    />
                  }
                  label={value.mode}
                />
              </Grid>
            ))}
          </Grid>
          <Grid item>
            <Divider />
          </Grid>
        </Grid>
      )}
    </Grid>
  )
}

Filters.propTypes = {
  filters: PropTypes.shape({
    language: PropTypes.array,
    level: PropTypes.array,
    learning_mode: PropTypes.array,
    category: PropTypes.array
  }),
  loading: PropTypes.bool,
  category: PropTypes.string,
  sliderBreakpoints: PropTypes.array,
  handleCategoryFilter: PropTypes.func,
  handleFilters: PropTypes.func
}

Filters.defaultProps = {
  loading: false
}

function ValueLabelComponent(props) {
  const { children, open, value } = props

  return (
    <Tooltip
      open={open}
      enterTouchDelay={0}
      placement="top"
      title={
        <Typography variant="body1">
          {getPluralOrSingular("day", value)}
        </Typography>
      }
    >
      {children}
    </Tooltip>
  )
}

ValueLabelComponent.propTypes = {
  children: PropTypes.element.isRequired,
  open: PropTypes.bool.isRequired,
  value: PropTypes.number.isRequired
}
